import { JobState, JobStateEvent, LoadState } from '@treadinc/horizon-api-spec';

import { OrderStatusFilter } from '~constants/enums';
import { Job, JobFormPropsWithRates } from '~hooks/useJob/models';
import { Load } from '~hooks/useLoad/models';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { RateDetailsDTO } from '~pages/Sales/Orders/NewOrderForm/schema';

import {
  activeOrderStates,
  doneOrderStates,
  externalOrderStates,
  inProgressOrderStates,
  pendingOrderStates,
} from './constants/orderStates';

export function getJobNextEvents(job: Job) {
  // We don't want any job to fall within any of the following states because they are
  // to be deprecated soon. Once that happen, this block should be removed.
  const deprecatedStates = [JobStateEvent.FLAG, JobStateEvent.SIGN_OFF];
  const nonRoutableStates = [JobStateEvent.ASSIGN];
  const filteredEvents = [...deprecatedStates, ...nonRoutableStates];

  return job.stateEvents.filter((stateEvent) => {
    return !filteredEvents.includes(stateEvent);
  }) as JobStateEvent[];
}

export function canAcceptJob(job: Job) {
  const isEditable = job.editable;
  const isSent = job.status === JobState.SENT;
  const isUnasigned = !job.driver && !job.vendorJobAssignment;

  return isEditable && isSent && isUnasigned;
}

export const getOrderStatesPerStatus = (status: OrderStatusFilter) => {
  switch (status) {
    case OrderStatusFilter.EXTERNAL:
      return externalOrderStates;
    case OrderStatusFilter.ACTIVE:
      return activeOrderStates;
    case OrderStatusFilter.PENDING:
      return pendingOrderStates;
    case OrderStatusFilter.INPROGRESS:
      return inProgressOrderStates;
    case OrderStatusFilter.DONE:
      return doneOrderStates;
    default:
      return [];
  }
};

/**
 * Helper function to determine if a load should have transitionable states
 * based on the job state
 */
export function getLoadTransitionableStates(load: Load, jobState: JobState) {
  // Only allow transition to completed when job is in specific states
  const allowedJobStates = [JobState.COMPLETED, JobState.ENDED, JobState.IN_REVIEW];

  if (!allowedJobStates.includes(jobState)) {
    return [];
  }

  // Only allow transition to completed if load is not already completed
  if (load.state === LoadState.COMPLETED) {
    return [];
  }

  return [LoadState.COMPLETED];
}

export interface RateSubmitDetails {
  data: RateDetailsDTO | undefined;
  addOnAssignments: AddOnAssignmentSchema[];
}

interface BuildJobEditRatePayloadInput {
  customerRateDetails: RateSubmitDetails;
  vendorOrDriverRateDetails: RateSubmitDetails;
  isAssignedToVendor: boolean;
  isAssignedToDriver: boolean;
}

type JobEditRatePayload = Pick<
  JobFormPropsWithRates,
  'customerAddOnAssignments' | 'vendorAddOnAssignments' | 'driverAddOnAssignments'
> &
  Partial<
    Pick<
      JobFormPropsWithRates,
      | 'customerRate'
      | 'customerRateType'
      | 'customerRateValue'
      | 'assigneeRate'
      | 'vendorPayCommissionPercentage'
      | 'driverPayCommissionPercentage'
    >
  >;

interface ComputeJobConditionsChangedInput {
  isDirtyEquipmentTypeName: boolean;
  isDirtyMaterial: boolean;
  defaultPickupSiteId: string | undefined;
  currentPickupSiteId: string | undefined;
  defaultDropoffSiteId: string | undefined;
  currentDropoffSiteId: string | undefined;
}

export function computeJobConditionsChanged({
  isDirtyEquipmentTypeName,
  isDirtyMaterial,
  defaultPickupSiteId,
  currentPickupSiteId,
  defaultDropoffSiteId,
  currentDropoffSiteId,
}: ComputeJobConditionsChangedInput): boolean {
  return Boolean(
    isDirtyEquipmentTypeName ||
    isDirtyMaterial ||
    defaultPickupSiteId !== currentPickupSiteId ||
    defaultDropoffSiteId !== currentDropoffSiteId,
  );
}

export function buildJobEditRatePayload({
  customerRateDetails,
  vendorOrDriverRateDetails,
  isAssignedToVendor,
  isAssignedToDriver,
}: BuildJobEditRatePayloadInput): JobEditRatePayload {
  const isAssigned = isAssignedToVendor || isAssignedToDriver;
  const commissionPercentage =
    vendorOrDriverRateDetails.data?.targetPayCommissionPercentage;

  const assigneeRate: JobFormPropsWithRates['assigneeRate'] | null =
    isAssigned &&
    vendorOrDriverRateDetails.data &&
    (vendorOrDriverRateDetails.data.targetRate !== undefined ||
      vendorOrDriverRateDetails.data.targetRateType !== undefined ||
      vendorOrDriverRateDetails.data.targetRateValue !== undefined)
      ? {
          target: isAssignedToVendor ? 'vendor' : 'driver',
          rate: vendorOrDriverRateDetails.data.targetRate ?? null,
          rateType: vendorOrDriverRateDetails.data.targetRateType ?? null,
          rateValue: vendorOrDriverRateDetails.data.targetRateValue ?? null,
        }
      : null;

  return {
    ...(isAssignedToVendor && commissionPercentage !== undefined
      ? { vendorPayCommissionPercentage: commissionPercentage }
      : {}),
    ...(isAssignedToDriver && commissionPercentage !== undefined
      ? { driverPayCommissionPercentage: commissionPercentage }
      : {}),
    ...(customerRateDetails.data
      ? {
          customerRate: customerRateDetails.data.targetRate ?? null,
          customerRateType: customerRateDetails.data.targetRateType ?? null,
          customerRateValue: customerRateDetails.data.targetRateValue ?? null,
        }
      : {}),
    ...(assigneeRate ? { assigneeRate } : {}),
    customerAddOnAssignments: customerRateDetails.addOnAssignments,
    vendorAddOnAssignments: isAssignedToVendor
      ? vendorOrDriverRateDetails.addOnAssignments
      : [],
    driverAddOnAssignments: isAssignedToDriver
      ? vendorOrDriverRateDetails.addOnAssignments
      : [],
  };
}
